import { ReactNode, ReactElement, lazy, Suspense } from 'react';
import _ from 'lodash';
import { IWidgetThingsClass } from "../Utils/IWidgetThingsClass";
import { IconURIPath, LazyWidget, WidgetLocalizationKeys } from "../WidgetsThingsStructs";
import { IWidgetDescription, WidgetDescriptionToPlegmaThing, IWidgetDataValidateResp } from "../../WidgetStructs";
import { Plegma, WebNode, WebThing, WebThingPort, WebThingPortType, } from "../../../../API";
import { LazyLoadingAnimation } from '@Primitive/Lazy';
import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import { BoardTheming } from '@UI/Dashboard/BoardTheming';
import { JsonSchemaValidator } from '@Utils/JsonSchemaValidator';
import { LocalizationMap } from '@Primitive/Localization/Localization';
import { defaultLocalizationValues, LocalizationKeys, PortKeys, ToolbarIconsIds, TreeData } from './TreeStruct';

const Tree = lazy(() => import('./TreeAsync'));
const TreeWidgetSettings = lazy(() => import('./TreeAsync').then(module => ({ default: module.TreeWidgetSettings })));
// ---------------------------------------------------------------------------------------------
export class TreeWidget extends IWidgetThingsClass<TreeData> {
    Render(): ReactNode {
        return <LazyWidget >
            <Tree />
        </LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): ReactElement<any> {
        const parent = this.Parent;
        return <Suspense fallback={<LazyLoadingAnimation />}>
            <TreeWidgetSettings
                key={'settings-' + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)}
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </Suspense>
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: TreeData): WebThingPort[] {
        let ports: WebThingPort[] = [
            new WebThingPort({
                Name: 'Data',
                PortKey: PortKeys.dataPort,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            }, WebThingPortType.Data, data => this.ValidateValues(PortKeys.dataPort, data)),
            new WebThingPort({
                Name: 'Insert or Update',
                PortKey: PortKeys.insertOrUpdate,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            }, WebThingPortType.Data, data => this.ValidateValues(PortKeys.insertOrUpdate, data)),
            new WebThingPort({
                Name: 'Select Item(s)',
                PortKey: PortKeys.selectItem,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Input,
                State: ''
            }, WebThingPortType.Data),
            new WebThingPort({
                Name: 'Selected Item(s)',
                PortKey: PortKeys.selectedItem,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),
            new WebThingPort({
                Name: 'Toolbar Button Clicked',
                PortKey: PortKeys.toolbarButtonClicked,
                Type: Plegma.ePortType.String,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger),
            new WebThingPort({
                Name: 'Pivot Dropdown Menu Clicked',
                PortKey: PortKeys.pivotDropdownButtonClicked,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger)
        ];

        if (widgetData.showToolbar) {
            _.forEach(widgetData.toolbarButtons, (btn) => {
                ports.push(new WebThingPort({
                    Name: btn.tooltip,
                    PortKey: btn.key,
                    Type: Plegma.ePortType.Boolean,
                    ioDirection: Plegma.ioPortDirection.Output,
                    State: 'False'
                }, WebThingPortType.Trigger));
            });
        }

        _.forEach(widgetData.itemButtons, (btn) => {
            ports.push(new WebThingPort({
                Name: btn.tooltip,
                PortKey: btn.key,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Output,
                State: ''
            }, WebThingPortType.Trigger));
        });

        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this.createThingPorts(widgetData).forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode.GetModule(this.WebModuleKey).UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as TreeData);

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): TreeData {
        const theme = BoardTheming.GetActiveTheme();
        return {
            showToolbar: false,
            toolbarBgColor: theme.genericToolbars.style.backgroundColor,
            toolbarIconColor: theme.genericToolbars.style.fontColor,
            supportExpandCollapse: false,
            supportSearch: false,
            searchbarBgColor: theme.genericToolbars.style.subBackgroundColor,
            searchbarTextColor: theme.genericToolbars.style.fontColor,
            toolbarButtons: [],
            itemButtons: [],
            supportPivot: false,
            initiallyExpanded: false,
            selectableFolders: false,
            pivotsConfig: [],
            noItemsMessage: 'No items',
            noMatchedItemsMessage: 'No matches',
            types: [],
            multipleSelection: false,
            treeBackgroundColor: "white",
            treeDropdownTextIconColor: 'black',
            treeFontColor: "black",
            treeSelectColor: theme.appToolbar.style.backgroundColor,
            readonly: false,
            supportTooltip: false,
            tooltipField: "tooltip", //by default tooltip field
            overrideGlobalTheme: false,
            supportRightButtons: false,
            toolbarIconOrdering: [
                { icon: "fas fa-search", id: ToolbarIconsIds.Search, label: "Search", visible: true },
                { icon: "fa fa-folder-open-o", id: ToolbarIconsIds.ExpandFolders, label: "Expand Folders", visible: true },
                { icon: "fa fa-folder-o", id: ToolbarIconsIds.CollapseFolders, label: "Collapse Folders", visible: true },
                { icon: "", id: ToolbarIconsIds.UserIcons, label: "Custom Icons", visible: true },
                { icon: "fas fa-indent", id: ToolbarIconsIds.Pivot, label: "Pivot", visible: true },
            ]
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return TreeWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        return {
            Title: 'Tree',
            WidgetTitle: '',
            Key: 'TreeWidget',
            Type: 'TreeWidget',
            Data: TreeWidget.GetDefaultWidgetDataStatic(),
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: IconURIPath + '/svg/tree.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary(): LocalizationMap {
        const res = {};
        const { showToolbar,
            toolbarButtons,
            supportExpandCollapse, supportSearch, supportPivot,
            noItemsMessage, noMatchedItemsMessage
        } = this.WidgetData;

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        if (!IsNullOrEmpty(noItemsMessage))
            res[LocalizationKeys.noItemsMessage] = noItemsMessage;

        if (!IsNullOrEmpty(noMatchedItemsMessage))
            res[LocalizationKeys.noMatchedItemsMessage] = noMatchedItemsMessage;

        // check if toolbar is shown
        if (showToolbar) {
            // localize search buttons
            if (supportSearch) {
                res[LocalizationKeys.showSearch] = _.clone(defaultLocalizationValues[LocalizationKeys.showSearch]);
                res[LocalizationKeys.hideSearch] = _.clone(defaultLocalizationValues[LocalizationKeys.hideSearch]);
            }

            // localize expand/collase buttons
            if (supportExpandCollapse) {
                res[LocalizationKeys.expandTree] = _.clone(defaultLocalizationValues[LocalizationKeys.expandTree]);
                res[LocalizationKeys.collapseTree] = _.clone(defaultLocalizationValues[LocalizationKeys.collapseTree]);
            }

            // Localize tooltip for pivoting.
            if (supportPivot)
                res[LocalizationKeys.pivotTree] = _.clone(defaultLocalizationValues[LocalizationKeys.pivotTree]);

            // localize custom buttons
            _.forEach(toolbarButtons, (btn) => {
                if (!IsNullOrEmpty(btn.tooltip))
                    res[getKeyForTooltip(btn.key)] = btn.tooltip;
            });
        }

        return res;
    }
    // ------------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        if (key == PortKeys.dataPort)
            return dataPortSchemaValidator().DataValidateString(value);
        else if (key == PortKeys.insertOrUpdate)
            return upsertPortSchemaValidator().DataValidateString(value);
        return { valid: true, errors: "" };
    }
    // ------------------------------------------------------------------------------------------------
    /**
     * Used to migrate data.
     *  - Set id in dropdown buttons (if any id is not defined)
     */
    public MigrateDefaultWidgetData(data: TreeData): TreeData {

        if (isDefined(data.itemButtons) &&
            data.itemButtons.length > 0 &&
            IsNullOrEmpty(data.itemButtons[0].id)) {
            _.forEach(data.itemButtons, (b, i) => {
                b.id = "Button " + (i + 1);
            });
        }

        _.forEach(data.pivotsConfig, pivotConfig => {
            _.forEach(pivotConfig.fields, field => {
                if (!isDefined(field.selectable)) {
                    field.selectable = true;
                }

                if (!isDefined(field.itemButtons)) {
                    field.itemButtons = [];
                }
            })
        })

        if (!isDefined(data.overrideGlobalTheme))
            data.overrideGlobalTheme = true;

        return data;
    }
    // ------------------------------------------------------------------------------------------------
}

// ------------------------------------------------------------------------------------------------
function getKeyForTooltip(key: string) { return 'toolbar_btn_' + key; }
// -------------------------------------------------------------------------------------------------
const validatorOpts = (isDataPort: boolean): object => {
    let required = ['id'];
    if (isDataPort)
        required.push('label');
    return {
        type: 'array',
        items: {
            type: 'object',
            properties: {
                id: { type: 'string' },
                label: { type: 'string' },
                type: { type: 'string' },
                dropdownIds: { type: 'array', items: { type: 'string' } }
            },
            required: required
        },
        minItems: 0
    };
}
// -------------------------------------------------------------------------------------------------
function dataPortSchemaValidator() {
    return JsonSchemaValidator.GetInstance('dataPortSchemaValidator', validatorOpts(true));
}
// -------------------------------------------------------------------------------------------------
function upsertPortSchemaValidator() {
    return JsonSchemaValidator.GetInstance('upsertPortSchemaValidator', validatorOpts(false));
}
// -------------------------------------------------------------------------------------------------
